import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { CardService, CardData } from '../services/cardService';
import { UserService } from '../services/userService';
import CardPreview from '../components/CardPreview';
import ColorPicker from '../components/ColorPicker';
import ProfilePhotoUpload from '../components/ProfilePhotoUpload';
import PortfolioGalleryUpload from '../components/PortfolioGalleryUpload';

// 앱 버전과 동일한 색상 옵션
const PREDEFINED_COLORS = [
  { name: 'pureBlack', value: '#000000' },
  { name: 'coolMint', value: '#00C2A8' },
  { name: 'deepBlue', value: '#1E3A8A' },
  { name: 'sunsetOrange', value: '#FF6B57' },
  { name: 'mutedViolet', value: '#7D6AFF' },
  { name: 'warmSand', value: '#F4D58D' },
  { name: 'modernBlue', value: '#3B82F6' },
  { name: 'vibrantGreen', value: '#10B981' },
  { name: 'coralRed', value: '#EF4444' },
  { name: 'royalPurple', value: '#8B5CF6' },
];

const BACKGROUND_COLORS = [
  { name: 'White', value: '#FFFFFF' },
  { name: 'Light Gray', value: '#F2F2F7' },
  { name: 'Light Blue', value: '#E6F3FF' },
  { name: 'Light Green', value: '#E6FFED' },
  { name: 'Light Pink', value: '#FFE6F0' },
  { name: 'Light Yellow', value: '#FFFACD' },
];

// 예약된 슬러그 목록
const RESERVED_SLUGS = [
  'qr', 'privacy-policy', 'terms', 'delete-account', 'assets', 'api',
  'login', 'register', 'dashboard', 'home', 'admin', 'system', 'app',
  'www', 'mail', 'ftp', 'smtp', 'pop', 'imap', 'webmail', 'cpanel',
  'phpmyadmin', 'wp-admin', 'wp-content', 'wp-includes', 'wordpress',
  'blog', 'forum', 'shop', 'store', 'cart', 'checkout', 'payment',
  'support', 'help', 'faq', 'contact', 'about', 'team', 'careers',
  'privacy', 'terms-of-service', 'legal', 'sitemap', 'robots',
  'favicon', 'manifest', 'service-worker', 'sw', 'pwa'
];

const CardCreatePage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    position: '',
    website: '',
    address: '',
    customPath: '',
    bio: '',
    linkedin: '',
    twitter: '',
    instagram: '',
    // 앱 버전과 동일한 추가 필드들
    instagramId: '',
    threadsId: '',
    youtubeId: '',
    linkedinId: '',
    facebookId: '',
    tiktokId: '',
    kakaoChannelUrl: '',
    companyAddress: '',
    memo: '',
    introText: '',
    servicesIntro: '',
    calendlyLink: '',
    feedbackFormLink: '',
    // 색상 관련 필드들
    cardButtonColor: '#007AFF',
    cardBackgroundColor: '#FFFFFF',
    // 프로필 사진 필드 추가
    photoUrl: '',
    // 포트폴리오 갤러리 필드 추가
    portfolioImages: [] as string[]
  });

  const [customUrlError, setCustomUrlError] = useState<string | null>(null);
  const [isCheckingUrl, setIsCheckingUrl] = useState(false);
  const [showEnglishWarning, setShowEnglishWarning] = useState(false);
  const [urlStatus, setUrlStatus] = useState<'idle' | 'checking' | 'available' | 'taken' | 'invalid' | 'reserved'>('idle');
  const [debounceTimerRef, setDebounceTimerRef] = useState<NodeJS.Timeout | null>(null);

  // 컴포넌트 언마운트 시 타이머 정리
  useEffect(() => {
    return () => {
      if (debounceTimerRef) {
        clearTimeout(debounceTimerRef);
      }
    };
  }, [debounceTimerRef]);

  // URL 유효성 검사 함수
  const validateUrl = (url: string): boolean => {
    const urlRegex = /^[a-z0-9-]{3,30}$/;
    const isValidFormat = urlRegex.test(url) && !url.startsWith('-') && !url.endsWith('-');
    const isReserved = RESERVED_SLUGS.includes(url.toLowerCase());
    return isValidFormat && !isReserved;
  };

  // 중복 체크 함수 개선
  const checkUrlAvailability = async (url: string) => {
    if (!validateUrl(url)) {
      if (RESERVED_SLUGS.includes(url.toLowerCase())) {
        setUrlStatus('reserved');
      } else {
        setUrlStatus('invalid');
      }
      return;
    }

    setUrlStatus('checking');
    setIsCheckingUrl(true);

    try {
      const isAvailable = await CardService.checkCustomUrlAvailability(url);
      setUrlStatus(isAvailable ? 'available' : 'taken');
    } catch (error: any) {
      console.error('URL 중복 체크 실패:', error);
      if (error.code === 'permission-denied' || error.message?.includes('permission')) {
        setUrlStatus('available');
      } else {
        setUrlStatus('available');
      }
    } finally {
      setIsCheckingUrl(false);
    }
  };

  // 상태 메시지 함수
  const getUrlStatusDisplay = () => {
    switch (urlStatus) {
      case 'checking':
        return { text: '확인 중...', color: 'text-blue-500' };
      case 'available':
        return { text: '사용 가능합니다!', color: 'text-green-500' };
      case 'taken':
        return { text: '이미 사용중인 URL입니다.', color: 'text-red-500' };
      case 'reserved':
        return { text: '예약된 슬러그입니다. 다른 이름을 사용해주세요.', color: 'text-red-500' };
      case 'invalid':
        return { text: '3-30자, 영문 소문자, 숫자, 하이픈만 사용 가능합니다.', color: 'text-red-500' };
      default:
        return { text: '', color: 'text-gray-500' };
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    // customPath 입력 시 영어 강제 적용 (앱 버전과 동일)
    if (name === 'customPath') {
      // 영어, 숫자, 하이픈만 남기고 모든 문자 제거
      const englishOnlyText = value.replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
      
      // 원본 텍스트와 필터링된 텍스트가 다르면 경고 표시
      if (value !== englishOnlyText) {
        setShowEnglishWarning(true);
        // 한글 입력 감지 시 경고 (3초 후 자동 숨김)
        setTimeout(() => {
          setShowEnglishWarning(false);
        }, 3000);
      }
      
      // 필터링된 텍스트로 설정
      setFormData(prev => ({
        ...prev,
        [name]: englishOnlyText
      }));
      
      setCustomUrlError(null);
      
      if (debounceTimerRef) {
        clearTimeout(debounceTimerRef);
      }

      if (englishOnlyText.trim()) {
        const newTimer = setTimeout(() => {
          checkUrlAvailability(englishOnlyText);
        }, 500);
        setDebounceTimerRef(newTimer);
      } else {
        setUrlStatus('idle');
      }
    } else {
      // 다른 필드들은 일반적으로 처리
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    console.log('🚀 [CardCreatePage] handleSubmit 호출됨 - 이벤트:', e.type);
    e.preventDefault();
    
    // 중복 클릭 방지
    if (loading) {
      console.log('🔄 [CardCreatePage] 이미 처리 중입니다. 중복 클릭 무시');
      return;
    }
    
    if (!user?.uid) {
      setError(t('createCardLoginMessage', 'Login required to create a card.'));
      return;
    }

    if (!formData.name || !formData.email) {
      setError(t('common.alert.missingInfoMessage', { fields: t('common.fields.name') + ', ' + t('common.fields.email') }));
      return;
    }

    // URL 필수 입력 체크를 submit 시에만 수행
    if (!formData.customPath.trim()) {
      setError(t('customUrl.required', 'Please enter a custom URL.'));
      return;
    }

    if (urlStatus === 'taken' || urlStatus === 'invalid' || urlStatus === 'reserved') {
      setError(getUrlStatusDisplay().text);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // 카드 생성 (앱 버전과 동일한 필드들 포함)
      const newCard: Omit<CardData, 'id'> = {
        ownerUid: user.uid,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || '',
        company: formData.company || '',
        position: formData.position || '',
        website: formData.website || '',
        address: formData.address || '',
        customPath: formData.customPath || '',
        bio: formData.bio || '',
        // 앱 버전과 동일한 추가 필드들
        instagramId: formData.instagramId || '',
        threadsId: formData.threadsId || '',
        youtubeId: formData.youtubeId || '',
        linkedinId: formData.linkedinId || '',
        facebookId: formData.facebookId || '',
        tiktokId: formData.tiktokId || '',
        kakaoChannelUrl: formData.kakaoChannelUrl || '',
        companyAddress: formData.companyAddress || '',
        memo: formData.memo || '',
        introText: formData.introText || '',
        servicesIntro: formData.servicesIntro || '',
        calendlyLink: formData.calendlyLink || '',
        feedbackFormLink: formData.feedbackFormLink || '',
                 // 색상 관련 필드들
         cardButtonColor: formData.cardButtonColor,
         cardBackgroundColor: formData.cardBackgroundColor,
         // 프로필 사진 필드
         photoUrl: formData.photoUrl || '',
        portfolioImages: formData.portfolioImages || [],
         socialLinks: {
           linkedin: formData.linkedin || '',
           twitter: formData.twitter || '',
           instagram: formData.instagram || ''
         },
        createdAt: new Date(),
        updatedAt: new Date()
      };

      await CardService.createCard(user.uid, newCard);
      
      // 사용자 카드 수 증가
      await UserService.incrementCardCount(user.uid);
      
      // 성공 시 커스텀 URL로 새 창에서 열기
      if (formData.customPath) {
        // 새 창에서 생성된 카드의 커스텀 URL 열기
        window.open(`https://scanvcf.com/${formData.customPath}`, '_blank');
        // 현재 창은 대시보드로 이동
        navigate('/dashboard', { 
          state: { message: t('common.saveSuccess') }
        });
      } else {
        // 커스텀 URL이 없으면 대시보드로 이동
        navigate('/dashboard', { 
          state: { message: t('common.saveSuccess') }
        });
      }
      
    } catch (error: any) {
      console.error('카드 생성 실패:', error);
      
      // URL 중복 에러 처리
      if (error.message === 'CUSTOM_URL_ALREADY_EXISTS') {
        setError(t('customUrl.taken'));
        // 기존 카드를 새 창에서 열기
        if (formData.customPath) {
          window.open(`https://scanvcf.com/${formData.customPath}`, '_blank');
        }
      } else {
        setError(t('common.cardProcessingError'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/dashboard');
  };

  // 수동 URL 중복 체크 함수
  const handleManualUrlCheck = async () => {
    if (!formData.customPath) return;
    
    await checkUrlAvailability(formData.customPath);
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600 mb-4">{t('createCardLoginMessage', 'Login required to create a card.')}</p>
          <button
            onClick={() => navigate('/login')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            {t('auth.login')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* 헤더 */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <h1 className="text-3xl font-bold text-gray-900">
              {t('cardEdit.title', 'Create New Card')}
            </h1>
            <button
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-800"
            >
              ✕ {t('common.cancel')}
            </button>
          </div>
        </div>

        {/* 메인 컨테이너 - 미리보기와 폼을 나란히 배치 */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* 미리보기 */}
          <div className="lg:sticky lg:top-8 lg:h-fit">
            <div className="bg-white rounded-lg shadow-md p-6 mb-6">
                             <h2 className="text-lg font-semibold text-gray-900 mb-4">
                 {t('profilePreview.title', 'Preview')}
               </h2>
              <CardPreview
                cardData={{
                  name: formData.name,
                  company: formData.company,
                  position: formData.position,
                  email: formData.email,
                  phone: formData.phone,
                  website: formData.website,
                  customPath: formData.customPath,
                  photoUrl: formData.photoUrl,
                  portfolioImages: formData.portfolioImages
                }}
                cardButtonColor={formData.cardButtonColor}
                cardBackgroundColor={formData.cardBackgroundColor}
              />
            </div>
          </div>

          {/* 폼 */}
          <div>
            <form onSubmit={handleSubmit} className="bg-white shadow rounded-lg">
          <div className="p-6 space-y-6">
            {/* 프로필 사진 업로드 */}
            <div className="mb-6">
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {t('cardEdit.profilePhoto', 'Profile Photo')}
               </h3>
              <ProfilePhotoUpload
                currentPhotoUrl={formData.photoUrl}
                onPhotoChange={(photoUrl) => setFormData(prev => ({ ...prev, photoUrl }))}
                userId={user.uid}
                disabled={loading}
              />
            </div>

            {/* 기본 정보 */}
            <div>
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {t('cardEdit.basicInfo', 'Basic Information')}
               </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-1">
                    {t('cardEdit.name')} **
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.setCustomValidity(t('paywall.required', 'Required'));
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.setCustomValidity('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.name', 'Example: John Doe')}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-1">
                    {t('cardEdit.email')} **
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    onInvalid={(e) => {
                      const target = e.target as HTMLInputElement;
                      if (target.validity.valueMissing) {
                        target.setCustomValidity(t('paywall.required', 'Required'));
                      } else if (target.validity.typeMismatch) {
                        target.setCustomValidity(t('auth.emailInvalid', 'Please enter a valid email address.'));
                      }
                    }}
                    onInput={(e) => {
                      const target = e.target as HTMLInputElement;
                      target.setCustomValidity('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="example@email.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('cardEdit.phone')}
                  </label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="010-1234-5678"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('cardEdit.website')}
                  </label>
                  <input
                    type="text"
                    name="website"
                    value={formData.website}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.website', 'example.com')}
                  />
                </div>
              </div>
            </div>

            {/* 커스텀 URL */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('customUrl.title', 'Custom URL')}
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-red-600 mb-1">
                    {t('customUrl.placeholder', 'Custom URL')} **
                  </label>
                  <div className="flex">
                    <span className="inline-flex items-center px-3 rounded-l-md border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                      scanvcf.com/
                    </span>
                    <div className="relative flex-1">
                      <input
                        type="text"
                        name="customPath"
                        value={formData.customPath}
                        onChange={handleInputChange}
                        className={`w-full px-3 py-2 border rounded-r-md focus:outline-none focus:ring-2 ${
                          urlStatus === 'taken' || urlStatus === 'invalid' || urlStatus === 'reserved'
                            ? 'border-red-300 focus:ring-red-500' 
                            : urlStatus === 'checking'
                            ? 'border-yellow-300 focus:ring-yellow-500'
                            : urlStatus === 'available'
                            ? 'border-green-300 focus:ring-green-500'
                            : 'border-gray-300 focus:ring-blue-500'
                        }`}
                        placeholder={t('customUrl.placeholder', 'yourname')}
                      />
                      {/* 로딩 스피너 */}
                      {urlStatus === 'checking' && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                        </div>
                      )}
                      {/* 성공 체크마크 */}
                      {urlStatus === 'available' && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          <svg className="h-4 w-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* 상태별 메시지 */}
                  {getUrlStatusDisplay().text && (
                    <p className={`mt-1 text-xs flex items-center ${getUrlStatusDisplay().color}`}>
                      {urlStatus === 'checking' && (
                        <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500 mr-1"></div>
                      )}
                      {urlStatus === 'available' && (
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                      {(urlStatus === 'taken' || urlStatus === 'invalid' || urlStatus === 'reserved') && (
                        <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      )}
                      {getUrlStatusDisplay().text}
                    </p>
                  )}
                  
                  {showEnglishWarning && (
                    <p className="mt-1 text-xs text-orange-500 flex items-center">
                      <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L5.082 15.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      {t('customUrl.englishOnly', '영문, 숫자, 하이픈만 입력 가능합니다.')}
                    </p>
                  )}
                  
                  <p className="mt-1 text-xs text-gray-500">
                    {t('customUrl.helpText', '3-30자, 영문 소문자, 숫자, 하이픈만 사용 가능합니다.')}
                  </p>
                </div>
              </div>
            </div>

            {/* 직장 정보 */}
            <div>
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {t('cardEdit.contactInfo', 'Contact Information')}
               </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t('cardEdit.company')}
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={t('placeholders.company', 'Example: ABC Company')}
                  />
                </div>
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.jobTitle', 'Position')}
                   </label>
                  <input
                    type="text"
                    name="position"
                    value={formData.position}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={t('placeholders.jobTitle', 'Example: Marketing Director')}
                  />
                </div>
                <div className="md:col-span-2">
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.address', 'Address')}
                   </label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.address', 'Example: 1 Jianguomenwai Street, Chaoyang District, Beijing')}
                  />
                </div>
              </div>
            </div>

            {/* 소셜 미디어 */}
            <div>
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {t('cardEdit.socialMedia', 'Social Media')}
               </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.instagram', 'Instagram')}
                   </label>
                  <input
                    type="text"
                    name="instagramId"
                    value={formData.instagramId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.instagramId', '@username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.threads', 'Threads')}
                   </label>
                  <input
                    type="text"
                    name="threadsId"
                    value={formData.threadsId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.threadsId', '@username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.youtube', 'YouTube')}
                   </label>
                  <input
                    type="text"
                    name="youtubeId"
                    value={formData.youtubeId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.youtubeId', '@username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.linkedin', 'LinkedIn')}
                   </label>
                  <input
                    type="text"
                    name="linkedinId"
                    value={formData.linkedinId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.linkedinId', 'username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.facebook', 'Facebook')}
                   </label>
                  <input
                    type="text"
                    name="facebookId"
                    value={formData.facebookId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.facebookId', 'username')}
                  />
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.tiktok', 'TikTok')}
                   </label>
                  <input
                    type="text"
                    name="tiktokId"
                    value={formData.tiktokId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.tiktokId', '@username')}
                  />
                </div>
                                 <div className="md:col-span-2">
                   <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.kakao', 'KakaoTalk Channel')}
                   </label>
                  <input
                    type="text"
                    name="kakaoChannelUrl"
                    value={formData.kakaoChannelUrl}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.kakaoChannelUrl', '채널 URL')}
                  />
                </div>
              </div>
            </div>

            {/* AI Introduction */}
            <div>
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {t('cardEdit.companyIntro', 'Company Introduction')}
               </h3>
              <div className="space-y-4">
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.selfIntro', 'Self Introduction')}
                   </label>
                  <textarea
                    name="introText"
                    value={formData.introText}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={t('placeholders.selfIntro', 'Enter your self-introduction...')}
                  />
                </div>
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.serviceIntro', 'Service Introduction')}
                   </label>
                  <textarea
                    name="servicesIntro"
                    value={formData.servicesIntro}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                         placeholder={t('placeholders.serviceIntro', 'Enter your service introduction...')}
                  />
                </div>
              </div>
            </div>

            {/* Memo */}
            <div>
                             <label className="block text-sm font-medium text-gray-700 mb-1">
                 {t('cardEdit.memo', 'Memo')}
               </label>
              <textarea
                name="memo"
                value={formData.memo}
                onChange={handleInputChange}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                     placeholder={t('placeholders.memo', 'Enter your memo...')}
              />
            </div>

            {/* Advanced Web Features */}
            <div>
                             <h3 className="text-lg font-medium text-gray-900 mb-4">
                 {t('cardEdit.miniWebsite', 'Mini Website')}
               </h3>
              <div className="space-y-4">
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.bookingLink', 'Booking Link')}
                   </label>
                  <input
                    type="text"
                    name="calendlyLink"
                    value={formData.calendlyLink}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.calendlyLink', 'calendly.com/yourusername')}
                  />
                                     <p className="text-sm text-gray-500 mt-1">{t('cardEdit.bookingTip', 'Enter booking links such as Calendly')}</p>
                </div>
                <div>
                                     <label className="block text-sm font-medium text-gray-700 mb-1">
                     {t('cardEdit.reviewLink', 'Review Link')}
                   </label>
                  <input
                    type="text"
                    name="feedbackFormLink"
                    value={formData.feedbackFormLink}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('placeholders.feedbackFormLink', 'forms.gle/example')}
                  />
                                     <p className="text-sm text-gray-500 mt-1">{t('cardEdit.reviewTip', 'Enter review links such as Google Forms')}</p>
                </div>
              </div>
            </div>

            {/* 포트폴리오 갤러리 */}
            <div className="mb-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('cardEdit.portfolioImages', 'Portfolio Images')}
              </h3>
              <PortfolioGalleryUpload
                currentImages={formData.portfolioImages}
                onImagesChange={(portfolioImages) => setFormData(prev => ({ ...prev, portfolioImages }))}
                userId={user.uid}
                disabled={loading}
              />
            </div>



            {/* 색상 선택 */}
                        <div>
              <h3 className="text-lg font-medium text-gray-900 mb-4">
                {t('cardColorSelection', 'Color Selection')}
              </h3>
              <div className="space-y-6">
                {/* 버튼 색상 선택 */}
                <div>
                  <ColorPicker
                    color={formData.cardButtonColor}
                    onColorChange={(color) => {
                      console.log('🎨 [CardCreatePage] 버튼 색상 변경:', color);
                      setFormData(prev => ({ ...prev, cardButtonColor: color }));
                    }}
                    showSwatches={true}
                    swatches={PREDEFINED_COLORS}
                    label={t('profilePreview.buttonColorLabel', 'Button Color')}
                  />
                </div>

                {/* 배경 색상 선택 */}
                <div>
                  <ColorPicker
                    color={formData.cardBackgroundColor}
                    onColorChange={(color) => {
                      console.log('🎨 [CardCreatePage] 배경 색상 변경:', color);
                      setFormData(prev => ({ ...prev, cardBackgroundColor: color }));
                    }}
                    showSwatches={true}
                    swatches={BACKGROUND_COLORS}
                    label={t('profilePreview.backgroundColorLabel', 'Background Color')}
                  />
                </div>
              </div>
            </div>



            {/* 에러 메시지 */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-4">
                <p className="text-red-600 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* 액션 버튼들 */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={loading || !!customUrlError}
              className={`px-6 py-2 rounded-md transition-colors ${
                loading || !!customUrlError
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {loading ? t('common.saving', 'Creating...') : t('home.createCard', 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
  </div>
  );
};

export default CardCreatePage;
